import { useEffect, useState, useCallback } from "react";
import { Link2, Plus, Copy, Send, Trash2, FileText, CheckCircle2, Clock, Loader2, CreditCard, Upload, ExternalLink, Download, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, API, getToken } from "@/lib/api";
import { toast } from "sonner";

const EXPIRY_OPTS = [
  { label: "24 hours", h: 24 },
  { label: "7 days", h: 168 },
  { label: "30 days", h: 720 },
  { label: "No expiry", h: 0 },
];

const StatusBadge = ({ status }) => {
  const map = {
    paid: { c: "bg-emerald-100 text-emerald-700", t: "Paid" },
    received: { c: "bg-emerald-100 text-emerald-700", t: "Files received" },
    pending: { c: "bg-amber-100 text-amber-700", t: "Pending" },
    completed: { c: "bg-emerald-100 text-emerald-700", t: "Completed" },
    active: { c: "bg-blue-100 text-blue-700", t: "Active" },
    revoked: { c: "bg-slate-100 text-slate-500", t: "Revoked" },
  };
  const m = map[status] || map.pending;
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${m.c}`}>{m.t}</span>;
};

export default function ActionLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", phone: "", email: "", expiry: 168,
    payEnabled: false, amount: "", description: "",
    fileEnabled: true, prompt: "Please upload your documents", deliver: false,
  });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/action-links"); setLinks(data); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) load(); }, 8000);
    return () => clearInterval(t);
  }, [load]); // real-time refresh (paused when tab hidden)
  // Realtime: WebSocket pushes an instant reload when a link changes (polling above is the fallback)
  useEffect(() => {
    const tok = getToken();
    if (!tok) return;
    let ws;
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
      ws = new WebSocket(`${base}/api/ws/portal?token=${encodeURIComponent(tok)}`);
      ws.onmessage = (e) => { try { const m = JSON.parse(e.data); if (m.type === "action_link" || m.type === "notification") load(); } catch { /* ignore */ } };
    } catch { /* ignore */ }
    return () => { try { ws && ws.close(); } catch { /* ignore */ } };
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.payEnabled && !form.fileEnabled) return toast.error("Enable payment and/or file upload.");
    if (form.payEnabled && !(Number(form.amount) > 0)) return toast.error("Enter a payment amount.");
    if (form.deliver && !form.phone) return toast.error("Add a phone number to send by SMS.");
    setBusy(true);
    try {
      const { data } = await api.post("/action-links", {
        customer_name: form.customer_name, phone: form.phone, email: form.email,
        expires_in_hours: Number(form.expiry),
        payment: { enabled: form.payEnabled, amount: form.payEnabled ? Number(form.amount) : null, description: form.description || "Payment" },
        file_upload: { enabled: form.fileEnabled, prompt: form.prompt },
        deliver: form.deliver ? "sms" : "none",
      });
      toast.success(form.deliver && data.delivered_via === "sms" ? "Link created & sent by SMS" : "Action link created");
      setOpen(false);
      setForm((f) => ({ ...f, customer_name: "", phone: "", email: "", amount: "", description: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create link");
    } finally { setBusy(false); }
  };

  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("Link copied"); };
  const resend = async (id) => { try { await api.post(`/action-links/${id}/resend`); toast.success("Sent by SMS"); } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); } };
  const revoke = async (id) => { try { await api.delete(`/action-links/${id}`); toast.success("Link revoked"); load(); } catch { toast.error("Failed"); } };
  const downloadFile = (linkId, fileId, name) => {
    const url = `${API}/action-links/${linkId}/files/${fileId}/download`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u); })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div data-testid="action-links-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Link2 className="h-6 w-6 text-blue-600" /> Action Links</h1>
          <p className="text-slate-500 text-sm mt-1">One secure link for customers to pay and/or upload documents. Your AI can send these automatically during calls.</p>
        </div>
        <Button data-testid="create-action-link-btn" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700 gap-2"><Plus className="h-4 w-4" /> Create link</Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-16 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : links.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center">
          <Link2 className="h-8 w-8 text-slate-300 mx-auto" />
          <p className="text-slate-500 mt-3">No action links yet. Create one to collect a payment or documents.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {links.map((l) => {
            const pay = l.actions?.payment || {};
            const up = l.actions?.file_upload || {};
            return (
              <div key={l.id} data-testid={`action-link-${l.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800">{l.customer_name || "Customer"}</span>
                      <StatusBadge status={l.expired ? "revoked" : l.status} />
                      {l.expired && <span className="text-xs text-rose-500 font-medium">Expired</span>}
                    </div>
                    <button onClick={() => copy(l.url)} className="mt-1 text-xs text-blue-600 hover:underline inline-flex items-center gap-1 truncate max-w-full" data-testid={`copy-link-${l.id}`}>
                      {l.url} <Copy className="h-3 w-3 shrink-0" />
                    </button>
                    <div className="flex flex-wrap gap-3 mt-3">
                      {pay.enabled && (
                        <div className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2">
                          <CreditCard className="h-4 w-4 text-slate-500" />
                          <span className="font-medium">{pay.currency || "GBP"} {Number(pay.amount).toFixed(2)}</span>
                          <StatusBadge status={pay.status} />
                        </div>
                      )}
                      {up.enabled && (
                        <div className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2">
                          <Upload className="h-4 w-4 text-slate-500" />
                          <span className="font-medium">{(up.files || []).length} file(s)</span>
                          <StatusBadge status={up.status} />
                        </div>
                      )}
                    </div>
                    {up.enabled && (up.files || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {up.files.map((f) => (
                          <button key={f.id} data-testid={`file-${f.id}`} onClick={() => downloadFile(l.id, f.id, f.original_filename)}
                            className="text-xs inline-flex items-center gap-1.5 border border-slate-200 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
                            <FileText className="h-3.5 w-3.5 text-blue-600" /> {f.original_filename} <Download className="h-3 w-3 text-slate-400" />
                          </button>
                        ))}
                      </div>
                    )}
                    {l.customer_note && (
                      <div data-testid={`note-${l.id}`} className="mt-3 flex items-start gap-2 text-sm bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-slate-700">
                        <MessageSquare className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                        <span><span className="font-medium">Note from customer:</span> {l.customer_note}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => copy(l.url)} className="gap-1.5"><Copy className="h-3.5 w-3.5" /> Copy</Button>
                    {l.phone && <Button variant="outline" size="sm" onClick={() => resend(l.id)} data-testid={`resend-${l.id}`} className="gap-1.5"><Send className="h-3.5 w-3.5" /> SMS</Button>}
                    <Button variant="ghost" size="sm" onClick={() => revoke(l.id)} className="text-rose-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create an action link</DialogTitle>
            <DialogDescription>Generate one secure link for the customer to pay and/or upload documents.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Customer name</Label><Input data-testid="al-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="mt-1" placeholder="Jane Smith" /></div>
              <div><Label className="text-xs">Phone (for SMS)</Label><Input data-testid="al-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" placeholder="07700 900123" /></div>
            </div>
            <div>
              <Label className="text-xs">Link expiry</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {EXPIRY_OPTS.map((o) => (
                  <button key={o.h} type="button" data-testid={`al-expiry-${o.h}`} onClick={() => set("expiry", o.h)}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${form.expiry === o.h ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>{o.label}</button>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-sm font-medium"><CreditCard className="h-4 w-4 text-blue-600" /> Request payment</Label>
                <Switch data-testid="al-pay-toggle" checked={form.payEnabled} onCheckedChange={(v) => set("payEnabled", v)} />
              </div>
              {form.payEnabled && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div><Label className="text-xs">Amount</Label><Input data-testid="al-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1" placeholder="50.00" /></div>
                  <div><Label className="text-xs">Description</Label><Input data-testid="al-desc" value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1" placeholder="Deposit" /></div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-sm font-medium"><Upload className="h-4 w-4 text-blue-600" /> Request file upload</Label>
                <Switch data-testid="al-file-toggle" checked={form.fileEnabled} onCheckedChange={(v) => set("fileEnabled", v)} />
              </div>
              {form.fileEnabled && (
                <div className="mt-3">
                  <Label className="text-xs">What should they upload?</Label>
                  <Textarea data-testid="al-prompt" value={form.prompt} onChange={(e) => set("prompt", e.target.value)} className="mt-1" rows={2} />
                  <p className="text-xs text-slate-400 mt-1">JPG, PNG, PDF, DOCX · up to 25 MB each.</p>
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Switch data-testid="al-deliver" checked={form.deliver} onCheckedChange={(v) => set("deliver", v)} /> Send to customer by SMS now
            </label>

            <Button data-testid="al-submit" onClick={create} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create link <ExternalLink className="h-4 w-4" /></>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
